"use client";

import { useEffect, useState } from "react";
import type { MarketTypeGroup } from "../types";

/**
 * Loads /api/sports-live/event/<slug>/markets and tracks the user's
 * current market-type tab + selected market + selected outcome
 * within that tab. The first market type returned by the API is
 * picked as the default tab on initial load.
 *
 * `initialOutcome` (from URL ?outcome=...) is matched case-insensitively
 * against outcome labels after the markets load — so a deep link from
 * the activity feed (e.g. "?outcome=timberwolves") lands on the right
 * side instead of always defaulting to outcome[0].
 *
 * Returns the markets-by-type response plus the three selection state
 * fields and their setters (used by the trading panel + outcome row).
 */
export function useEventMarkets(
  slug: string | null | undefined,
  initialOutcome?: string | null,
  // SSR-seeded markets-by-type (same shape the API returns) so the price bar
  // and market tabs paint on first render instead of waiting for the client
  // fetch. The effect below still refetches to revalidate.
  initialMarketsByType?: Record<string, MarketTypeGroup> | null,
) {
  const [marketsByType, setMarketsByType] = useState<Record<string, MarketTypeGroup> | null>(
    initialMarketsByType || null,
  );
  const [activeMarketType, setActiveMarketType] = useState(
    initialMarketsByType ? Object.keys(initialMarketsByType)[0] || "moneyline" : "moneyline",
  );
  const [selectedMarketIdx, setSelectedMarketIdx] = useState(0);
  const [selectedOutcomeIdx, setSelectedOutcomeIdx] = useState(0);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;

    fetch(`/api/sports-live/event/${encodeURIComponent(slug)}/markets`)
      .then(r => r.json())
      .then(data => {
        if (!cancelled && data.success && data.marketsByType) {
          setMarketsByType(data.marketsByType);
          const firstType = Object.keys(data.marketsByType)[0];
          if (firstType) {
            setActiveMarketType(firstType);
            setSelectedMarketIdx(0);

            // Match initialOutcome against the moneyline's first market
            // outcomes. Falls back to 0 if no match (typo, archived
            // outcome, multi-market type that doesn't expose this team).
            const desired = (initialOutcome || "").toLowerCase().trim();
            if (desired) {
              const firstMarket = data.marketsByType[firstType]?.markets?.[0];
              const outcomes: string[] = firstMarket?.outcomes || [];
              const idx = outcomes.findIndex((o: string) => o.toLowerCase().trim() === desired);
              setSelectedOutcomeIdx(idx >= 0 ? idx : 0);
            } else {
              setSelectedOutcomeIdx(0);
            }
          }
        }
      })
      .catch(() => {});

    return () => { cancelled = true; };
  }, [slug, initialOutcome]);

  return {
    marketsByType,
    activeMarketType,
    setActiveMarketType,
    selectedMarketIdx,
    setSelectedMarketIdx,
    selectedOutcomeIdx,
    setSelectedOutcomeIdx,
  };
}
